import React from 'react';
import { useTheme } from '@/contexts/ThemeContext';

export interface TerraActivityItem {
    terra_activity_id: string;
    provider: string;
    activity_kind: string;
    display_label: string;
    start_time: string;
    duration_s: number;
    distance_m: number | null;
    calories: number | null;
    hr_avg: number | null;
    hr_max: number | null;
    pace_s_per_km: number | null;
    elevation_gain_m: number | null;
}

export interface DayData {
    garmin?: {
        steps: number | null;
        distance_m: number | null;
        calories: number | null;
        hr_avg: number | null;
        resting_hr: number | null;
    };
    sleep?: {
        total_minutes: number | null;
        deep_minutes: number | null;
        rem_minutes: number | null;
        light_minutes: number | null;
        efficiency: number | null;
        quality_score: number | null;
    };
    catapult?: Array<{
        id: string;
        name: string;
        duration_min: number | null;
        athlete_count: number | null;
        distance_m: number | null;
        player_load: number | null;
    }>;
    hawkin?: Array<{
        id: string;
        test_type: string;
        athlete_name: string;
        jump_height_m: number | null;
        peak_force_n: number | null;
        rsi: number | null;
        lr_braking_pct: number | null;
        lr_propulsive_pct: number | null;
    }>;
    terra_activities?: TerraActivityItem[];
}

interface DayCellProps {
    date: number;
    dateStr: string;
    data: DayData | null;
    isToday: boolean;
    isCurrentMonth: boolean;
    isWeekView: boolean;
    onClick: (dateStr: string) => void;
    enabledSources: Set<string>;
}

export const PROVIDER_LOGOS = {
    garmin: { src: '/logos/garmin/garmin-connect-icon.png', alt: 'Garmin' },
    terra_activity: { src: '/logos/garmin/garmin-connect-icon.png', alt: 'Activity' },
    sleep: {
        light: '/logos/whoop/Logo/Circle/WHOOP Circle Black.svg',
        dark: '/logos/whoop/Logo/Circle/WHOOP Circle White.svg',
        alt: 'Sleep',
    },
    catapult: {
        light: '/logos/catapult/catapult-icon-black.webp',
        dark: '/logos/catapult/catapult-icon-white.webp',
        alt: 'Catapult',
    },
    hawkin: {
        light: '/logos/hawkin/hawkin-icon-black.webp',
        dark: '/logos/hawkin/hawkin-icon-white.webp',
        alt: 'Hawkin',
    },
} as const;

export const ProviderLogo: React.FC<{ provider: keyof typeof PROVIDER_LOGOS; size?: number }> = ({ provider, size = 12 }) => {
    const { isDark } = useTheme();
    const logo = PROVIDER_LOGOS[provider];
    const src = 'src' in logo ? logo.src : (isDark ? logo.dark : logo.light);
    return <img src={src} alt={logo.alt} className="flex-shrink-0 object-contain" style={{ width: size, height: size }} />;
};

function fmtK(n: number): string {
    return n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n);
}

function fmtDist(m: number): string {
    if (m >= 1000) return `${(m / 1000).toFixed(1)}km`;
    return `${Math.round(m)}m`;
}

function fmtMin(mins: number | null | undefined): string {
    if (mins == null) return '-';
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

function fmtSec(secs: number | null | undefined): string {
    if (secs == null) return '-';
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

function fmtPace(secsPerKm: number | null | undefined): string {
    if (secsPerKm == null) return '-';
    const m = Math.floor(secsPerKm / 60);
    const s = Math.round(secsPerKm % 60);
    return `${m}:${s < 10 ? '0' : ''}${s} /km`;
}

// ── Month-view compact card ─────────────────────────────────────────
interface GroupCardProps {
    provider: keyof typeof PROVIDER_LOGOS;
    value: string;
    label: string;
    isDark: boolean;
}

const GroupCard: React.FC<GroupCardProps> = ({ provider, value, label, isDark }) => (
    <div
        className="w-full flex-shrink-0 rounded-md flex items-center gap-1.5 px-2"
        style={{
            backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
            height: 'calc((100% - 24px) / 2)',
            minHeight: 35,
        }}
    >
        <ProviderLogo provider={provider} size={16} />
        <div className="text-lg font-bold cgm-text-primary leading-none">{value}</div>
        <div className="text-[9px] cgm-text-secondary leading-none">{label}</div>
    </div>
);

// ── Week-view detail section (mirrors DayDetail sidebar) ────────────
const WeekStat: React.FC<{ label: string; value: string | number | null | undefined }> = ({ label, value }) => {
    if (value == null || value === '') return null;
    return (
        <div className="flex justify-between py-0.5">
            <span className="text-[11px] cgm-text-secondary">{label}</span>
            <span className="text-[11px] font-medium cgm-text-primary">{value}</span>
        </div>
    );
};

const WeekSection: React.FC<{
    provider: keyof typeof PROVIDER_LOGOS;
    title: string;
    isDark: boolean;
    children: React.ReactNode;
}> = ({ provider, title, isDark, children }) => (
    <div
        className="rounded-md p-2 flex-shrink-0"
        style={{ backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)' }}
    >
        <div className="flex items-center gap-1.5 mb-1">
            <ProviderLogo provider={provider} size={14} />
            <span className="text-xs font-semibold cgm-text-primary">{title}</span>
        </div>
        {children}
    </div>
);

// ── Main component ──────────────────────────────────────────────────
const DayCell: React.FC<DayCellProps> = ({ date, data, isToday, isCurrentMonth, isWeekView, onClick, dateStr, enabledSources }) => {
    const { isDark } = useTheme();

    const activeSources: (keyof typeof PROVIDER_LOGOS)[] = [];
    if (enabledSources.has('garmin') && data?.garmin) activeSources.push('garmin');
    if (enabledSources.has('terra_activity') && data?.terra_activities?.length) activeSources.push('terra_activity');
    if (enabledSources.has('sleep') && data?.sleep) activeSources.push('sleep');
    if (enabledSources.has('catapult') && data?.catapult?.length) activeSources.push('catapult');
    if (enabledSources.has('hawkin') && data?.hawkin?.length) activeSources.push('hawkin');

    // ── Month view: compact cards ──────────────────────────────────
    const monthCards: React.ReactNode[] = [];
    if (!isWeekView) {
        if (activeSources.includes('garmin') && data?.garmin) {
            const sleepMins = data?.sleep?.total_minutes;
            if (sleepMins != null) {
                monthCards.push(<GroupCard key="garmin" provider="garmin" value={`${Math.round(sleepMins / 60)}h`} label="sleep" isDark={isDark} />);
            } else {
                const steps = data.garmin.steps;
                if (steps != null) monthCards.push(<GroupCard key="garmin" provider="garmin" value={fmtK(steps)} label="steps" isDark={isDark} />);
            }
        }
        if (activeSources.includes('terra_activity') && data?.terra_activities?.length) {
            data.terra_activities.forEach((act, i) => {
                const val = act.distance_m ? fmtDist(act.distance_m) : `${Math.round(act.duration_s / 60)}m`;
                monthCards.push(
                    <GroupCard key={`terra-act-${i}`} provider="terra_activity" value={val} label={act.display_label} isDark={isDark} />
                );
            });
        }
        if (activeSources.includes('sleep') && data?.sleep) {
            const mins = data.sleep.total_minutes;
            if (mins != null) monthCards.push(<GroupCard key="sleep" provider="sleep" value={`${Math.round(mins / 60)}h`} label="sleep" isDark={isDark} />);
        }
        if (activeSources.includes('catapult') && data?.catapult?.length) {
            data.catapult.forEach((sess, i) => {
                monthCards.push(
                    <GroupCard key={`catapult-${i}`} provider="catapult" value={sess.distance_m != null ? fmtDist(sess.distance_m) : '—'} label={sess.name || 'session'} isDark={isDark} />
                );
            });
        }
        if (activeSources.includes('hawkin') && data?.hawkin?.length) {
            data.hawkin.forEach((test, i) => {
                const asym = test.lr_propulsive_pct ?? test.lr_braking_pct;
                monthCards.push(
                    <GroupCard key={`hawkin-${i}`} provider="hawkin" value={asym != null ? `${asym > 0 ? '+' : ''}${asym.toFixed(1)}%` : '—'} label="L/R asym" isDark={isDark} />
                );
            });
        }
    }

    // ── Week view: detailed sections ───────────────────────────────
    const weekSections: React.ReactNode[] = [];
    if (isWeekView) {
        if (activeSources.includes('garmin') && data?.garmin) {
            const g = data.garmin;
            weekSections.push(
                <WeekSection key="garmin" provider="garmin" title="Daily Activity" isDark={isDark}>
                    <WeekStat label="Steps" value={g.steps?.toLocaleString()} />
                    <WeekStat label="Distance" value={g.distance_m ? `${(g.distance_m / 1000).toFixed(1)} km` : null} />
                    <WeekStat label="Calories" value={g.calories ? Math.round(g.calories) : null} />
                    <WeekStat label="Avg HR" value={g.hr_avg ? `${Math.round(g.hr_avg)} bpm` : null} />
                    <WeekStat label="Resting HR" value={g.resting_hr ? `${Math.round(g.resting_hr)} bpm` : null} />
                </WeekSection>
            );
        }
        if (activeSources.includes('terra_activity') && data?.terra_activities?.length) {
            data.terra_activities.forEach((act, i) => {
                weekSections.push(
                    <WeekSection key={`terra-act-${i}`} provider="terra_activity" title={act.display_label} isDark={isDark}>
                        <WeekStat label="Duration" value={fmtSec(act.duration_s)} />
                        <WeekStat label="Distance" value={act.distance_m ? `${(act.distance_m / 1000).toFixed(1)} km` : null} />
                        <WeekStat label="Calories" value={act.calories ? Math.round(act.calories) : null} />
                        <WeekStat label="Avg HR" value={act.hr_avg ? `${Math.round(act.hr_avg)} bpm` : null} />
                        <WeekStat label="Max HR" value={act.hr_max ? `${Math.round(act.hr_max)} bpm` : null} />
                        <WeekStat label="Pace" value={fmtPace(act.pace_s_per_km)} />
                        <WeekStat label="Elev Gain" value={act.elevation_gain_m ? `${Math.round(act.elevation_gain_m)} m` : null} />
                    </WeekSection>
                );
            });
        }
        if (activeSources.includes('sleep') && data?.sleep) {
            const s = data.sleep;
            weekSections.push(
                <WeekSection key="sleep" provider="sleep" title="Sleep" isDark={isDark}>
                    <WeekStat label="Duration" value={fmtMin(s.total_minutes)} />
                    <WeekStat label="Deep" value={fmtMin(s.deep_minutes)} />
                    <WeekStat label="REM" value={fmtMin(s.rem_minutes)} />
                    <WeekStat label="Light" value={fmtMin(s.light_minutes)} />
                    <WeekStat label="Efficiency" value={s.efficiency != null ? `${s.efficiency}%` : null} />
                </WeekSection>
            );
        }
        if (activeSources.includes('catapult') && data?.catapult?.length) {
            data.catapult.forEach((sess, i) => {
                weekSections.push(
                    <WeekSection key={`catapult-${i}`} provider="catapult" title={sess.name || 'Session'} isDark={isDark}>
                        <WeekStat label="Duration" value={sess.duration_min ? `${sess.duration_min} min` : null} />
                        <WeekStat label="Distance" value={sess.distance_m ? `${(sess.distance_m / 1000).toFixed(1)} km` : null} />
                        <WeekStat label="Player Load" value={sess.player_load} />
                        <WeekStat label="Athletes" value={sess.athlete_count} />
                    </WeekSection>
                );
            });
        }
        if (activeSources.includes('hawkin') && data?.hawkin?.length) {
            data.hawkin.forEach((t, i) => {
                const asym = t.lr_propulsive_pct ?? t.lr_braking_pct;
                weekSections.push(
                    <WeekSection key={`hawkin-${i}`} provider="hawkin" title={t.test_type || 'Test'} isDark={isDark}>
                        {t.athlete_name && <WeekStat label="Athlete" value={t.athlete_name} />}
                        <WeekStat label="Jump Height" value={t.jump_height_m != null ? `${(t.jump_height_m * 100).toFixed(1)} cm` : null} />
                        <WeekStat label="Peak Force" value={t.peak_force_n != null ? `${Math.round(t.peak_force_n)} N` : null} />
                        <WeekStat label="RSI" value={t.rsi != null ? t.rsi.toFixed(3) : null} />
                        <WeekStat label="L/R Asym" value={asym != null ? `${asym > 0 ? '+' : ''}${asym.toFixed(1)}%` : null} />
                    </WeekSection>
                );
            });
        }
    }

    const content = isWeekView ? weekSections : monthCards;

    return (
        <button
            onClick={() => onClick(dateStr)}
            className={`
                w-full h-full flex flex-col text-left transition-all duration-150 overflow-hidden p-1
                ${isCurrentMonth ? '' : 'opacity-25'}
                ${isToday ? 'ring-2 ring-blue-500/50 ring-inset' : ''}
                hover:ring-2 hover:ring-blue-400/40 hover:ring-inset cursor-pointer
            `}
        >
            {/* Top row: date (left) + source logos (right) */}
            <div className="flex items-start justify-between flex-shrink-0 mb-0.5">
                <span className={`text-sm font-bold leading-none ${isToday ? 'text-blue-500' : 'cgm-text-primary'}`}>
                    {date}
                </span>
                {activeSources.length > 0 && (
                    <div className="flex gap-0.5 items-center flex-shrink-0">
                        {activeSources.map(s => <ProviderLogo key={s} provider={s} size={14} />)}
                    </div>
                )}
            </div>

            {/* Content area */}
            {content.length > 0 && (
                <div className="flex-1 min-h-0 mt-0.5 flex flex-col gap-0.5 overflow-y-auto overflow-x-hidden scrollbar-none">
                    {content}
                </div>
            )}
        </button>
    );
};

export default DayCell;
